import { ContractUploadFiles } from "@/pages/contracts/types";

export type ContractFieldRawValue =
  | string
  | number
  | boolean
  | string[]
  | ContractUploadFiles
  | null;

// Committed-value fields are nullable: clearing them inline reverts to the
// service-calculated value. The inline ArrMrrEditableField reports a cleared
// input as 0 (Number("") === 0), so a 0 there must collapse to null. The
// Financial Recognition dialog, by contrast, lets users enter an explicit
// recognized 0 that must persist as a real override — those writes opt out
// via preserveExplicitZero.
export const NULLABLE_MONETARY_FIELDS = [
  "carrLocal",
  "cmrrLocal",
  "carr",
  "cmrr"
];

export interface ContractFieldValueOptions {
  preserveExplicitZero?: boolean;
}

// Normalizes a raw field value from an editor into the value sent to the API:
// blank strings become null, numeric strings become numbers, and a cleared
// nullable-monetary field collapses to null unless an explicit zero is kept.
export const coerceContractFieldValue = (
  field: string,
  value: ContractFieldRawValue,
  options?: ContractFieldValueOptions
): ContractFieldRawValue => {
  const coerced =
    typeof value === "string" && value.trim() === ""
      ? null
      : typeof value === "string" && Number.isFinite(Number(value))
        ? Number(value)
        : value;
  if (
    !options?.preserveExplicitZero &&
    NULLABLE_MONETARY_FIELDS.includes(field) &&
    coerced === 0
  ) {
    return null;
  }
  return coerced;
};

// Applies coerceContractFieldValue to every field in a batched update so that
// additional fields carried alongside the primary one (e.g. the Financial
// Recognition dialog saving several metrics at once) get the same normalization
// and explicit-zero handling instead of being forwarded raw.
export const coerceContractFields = (
  fields: Record<string, string | number | boolean | null> | undefined,
  options?: ContractFieldValueOptions
): Record<string, string | number | boolean | null> =>
  Object.fromEntries(
    Object.entries(fields ?? {}).map(([field, value]) => [
      field,
      coerceContractFieldValue(field, value, options)
    ])
  ) as Record<string, string | number | boolean | null>;
